import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Plus, Edit, Trash2, Save, X, Users, Calendar, CheckCircle } from "lucide-react";
import { examApi, clientApi, Exam, Client } from "@/services/api";
import { toast } from "@/hooks/use-toast";

interface ExamTemplate {
  id: string;
  title: string;
  questions: string[];
}

const Settings = () => {
  const { examId } = useParams<{ examId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const [selectedExam, setSelectedExam] = useState<string | null>(examId || null);
  const [isEditing, setIsEditing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const [examTemplates, setExamTemplates] = useState<Exam[]>([]);
  const [assignedExams, setAssignedExams] = useState<Exam[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [assigningExam, setAssigningExam] = useState<Exam | null>(null);
  const [selectedClients, setSelectedClients] = useState<string[]>([]);
  const [dueDate, setDueDate] = useState<string>("");

  const [editingExam, setEditingExam] = useState<Exam | null>(null);
  const [newExam, setNewExam] = useState<ExamTemplate>({
    id: "",
    title: "",
    questions: ["", "", "", "", ""]
  });

  // Load exam templates and clients on component mount
  useEffect(() => {
    loadExamTemplates();
    loadClients();
    loadAssignedExams();
  }, []);

  // Update selected exam when URL changes
  useEffect(() => {
    if (examId) {
      setSelectedExam(examId);
    } else {
      setSelectedExam(null);
    }
  }, [examId]);

  const loadExamTemplates = async () => {
    try {
      setLoading(true);
      const templates = await examApi.getExamTemplates();
      setExamTemplates(templates);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load exam templates",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadClients = async () => {
    try {
      const clientList = await clientApi.getAll();
      setClients(clientList);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load clients",
        variant: "destructive",
      });
    }
  };

  const loadAssignedExams = async () => {
    try {
      const assigned = await examApi.getAllAssignedExams();
      setAssignedExams(assigned);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load assigned exams",
        variant: "destructive",
      });
    }
  };

  const handleExamClick = (examId: string) => {
    navigate(`/LODYSettings/exam/${examId}`);
    setIsEditing(false);
    setIsAssigning(false);
  };

  const handleBackToExams = () => {
    navigate('/LODYSettings');
    setIsEditing(false);
    setIsCreating(false);
    setIsAssigning(false);
    setEditingExam(null);
    setAssigningExam(null);
    setSelectedClients([]);
    setDueDate("");
    setNewExam({
      id: "",
      title: "",
      questions: ["", "", "", "", ""]
    });
  };

  const handleEditExam = () => {
    const exam = examTemplates.find(e => e._id === selectedExam);
    if (exam) {
      setEditingExam({ ...exam });
      setIsEditing(true);
    }
  };

  const handleSaveEdit = async () => {
    if (editingExam) {
      try {
        const questions = editingExam.questions.map(q => q.question);
        await examApi.updateExamTemplate(editingExam._id!, {
          title: editingExam.title,
          description: editingExam.description,
          questions,
          category: editingExam.category,
          difficulty: editingExam.difficulty,
          estimatedTime: editingExam.estimatedTime
        });

        await loadExamTemplates();
        setIsEditing(false);
        setEditingExam(null);
        toast({
          title: "Success",
          description: "Exam updated successfully",
        });
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to update exam",
          variant: "destructive",
        });
      }
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditingExam(null);
  };

  const handleCreateNewExam = () => {
    setIsCreating(true);
    setNewExam({
      id: `exam${Date.now()}`,
      title: "",
      questions: ["", "", "", "", ""]
    });
  };

  const handleSaveNewExam = async () => {
    if (newExam.title.trim() && newExam.questions.some(q => q.trim())) {
      try {
        await examApi.createExamTemplate({
          title: newExam.title,
          questions: newExam.questions.filter(q => q.trim()),
          category: "General",
          difficulty: "medium",
          estimatedTime: 30
        });

        await loadExamTemplates();
        setIsCreating(false);
        setNewExam({
          id: "",
          title: "",
          questions: ["", "", "", "", ""]
        });
        toast({
          title: "Success",
          description: "Exam created successfully",
        });
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to create exam",
          variant: "destructive",
        });
      }
    }
  };

  const handleCancelCreate = () => {
    setIsCreating(false);
    setNewExam({
      id: "",
      title: "",
      questions: ["", "", "", "", ""]
    });
  };

  const handleDeleteExam = async (examId: string) => {
    try {
      await examApi.deleteExamTemplate(examId);
      await loadExamTemplates();
      if (selectedExam === examId) {
        setSelectedExam(null);
        setIsEditing(false);
      }
      toast({
        title: "Success",
        description: "Exam deleted successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete exam",
        variant: "destructive",
      });
    }
  };

  const handleAssignExam = () => {
    const exam = examTemplates.find(e => e._id === selectedExam);
    if (exam) {
      setAssigningExam(exam);
      setIsAssigning(true);
    }
  };

  const handleSaveAssignment = async () => {
    if (!assigningExam) {
      toast({
        title: "Error",
        description: "No exam selected for assignment",
        variant: "destructive",
      });
      return;
    }

    if (selectedClients.length === 0) {
      toast({
        title: "Error",
        description: "Please select at least one client to assign the exam to",
        variant: "destructive",
      });
      return;
    }

    // Validate that all selected clients exist
    const validClients = clients.filter(client => selectedClients.includes(client._id!));
    if (validClients.length !== selectedClients.length) {
      toast({
        title: "Error",
        description: "Some selected clients are no longer valid. Please refresh and try again.",
        variant: "destructive",
      });
      return;
    }

    try {
      console.log('Assigning exam:', assigningExam._id, 'to clients:', selectedClients);
      console.log('Valid clients:', validClients.map(c => c.name));

      const result = await examApi.assignExamToClients(assigningExam._id!, {
        clientIds: selectedClients,
        dueDate: dueDate || undefined
      });

      console.log('Assignment result:', result);

      // Reload assigned exams to show the new assignments
      await loadAssignedExams();

      setIsAssigning(false);
      setAssigningExam(null);
      setSelectedClients([]);
      setDueDate("");
      toast({
        title: "Success",
        description: `Exam "${assigningExam.title}" assigned successfully to ${selectedClients.length} client(s)`,
      });
    } catch (error: any) {
      console.error('Assignment error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to assign exam",
        variant: "destructive",
      });
    }
  };

  const handleUpdateQuestion = (index: number, value: string, isEditing: boolean) => {
    if (isEditing && editingExam) {
      const updatedQuestions = [...editingExam.questions];
      updatedQuestions[index] = { ...updatedQuestions[index], question: value };
      setEditingExam({ ...editingExam, questions: updatedQuestions });
    } else if (!isEditing && newExam) {
      const updatedQuestions = [...newExam.questions];
      updatedQuestions[index] = value;
      setNewExam({ ...newExam, questions: updatedQuestions });
    }
  };

  const handleAddQuestion = (isEditing: boolean) => {
    if (isEditing && editingExam) {
      setEditingExam({
        ...editingExam,
        questions: [...editingExam.questions, { question: "", order: editingExam.questions.length + 1 }]
      });
    } else if (!isEditing && newExam) {
      setNewExam({ ...newExam, questions: [...newExam.questions, ""] });
    }
  };

  const handleRemoveQuestion = (index: number, isEditing: boolean) => {
    if (isEditing && editingExam) {
      const updatedQuestions = editingExam.questions.filter((_, i) => i !== index);
      setEditingExam({ ...editingExam, questions: updatedQuestions });
    } else if (!isEditing && newExam) {
      const updatedQuestions = newExam.questions.filter((_, i) => i !== index);
      setNewExam({ ...newExam, questions: updatedQuestions });
    }
  };

  const selectedExamData = examTemplates.find(exam => exam._id === selectedExam);

  // Check if we're on an exam page
  const isOnExamPage = location.pathname.includes('/exam/');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading exam templates...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Settings</h1>
        <p className="text-muted-foreground">
          Manage your assignments and notification preferences
        </p>
      </div>

      <Tabs defaultValue="assignments" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="assignments">Assignments</TabsTrigger>
          <TabsTrigger value="completed">Completed</TabsTrigger>
        </TabsList>

        <TabsContent value="assignments" className="mt-6">
          {!selectedExam && !isCreating && !isAssigning ? (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold">Exam Templates</h2>
                <Button onClick={handleCreateNewExam} className="flex items-center gap-2">
                  <Plus className="w-4 h-4" />
                  Create New Exam
                </Button>
              </div>

              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {examTemplates.map((exam) => (
                  <Card
                    key={exam._id}
                    className={`cursor-pointer hover:shadow-md transition-shadow ${selectedExam === exam._id ? 'ring-2 ring-primary' : ''
                      }`}
                  >
                    <CardHeader>
                      <CardTitle className="text-lg">{exam.title}</CardTitle>
                      <CardDescription>
                        {exam.questions.length} questions
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground mb-4">
                        Click to view and complete this exam
                      </p>
                      <div className="flex gap-2">
                        <Button
                          className="flex-1"
                          onClick={() => handleExamClick(exam._id!)}
                        >
                          Start Exam
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/LODYSettings/exam/${exam._id}`);
                            setTimeout(() => handleEditExam(), 100);
                          }}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/LODYSettings/exam/${exam._id}`);
                            setTimeout(() => handleAssignExam(), 100);
                          }}
                        >
                          <Users className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteExam(exam._id!);
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ) : isCreating ? (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Create New Exam</CardTitle>
                    <CardDescription>
                      Add a new exam template
                    </CardDescription>
                  </div>
                  <Button variant="outline" onClick={handleCancelCreate}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="new-exam-title">Exam Title</Label>
                  <Input
                    id="new-exam-title"
                    value={newExam.title}
                    onChange={(e) => setNewExam({ ...newExam, title: e.target.value })}
                    placeholder="Enter exam title"
                  />
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label>Questions</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleAddQuestion(false)}
                      className="flex items-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Add Question
                    </Button>
                  </div>
                  {newExam.questions.map((question, index) => (
                    <div key={index} className="space-y-2 p-4 border rounded-lg">
                      <div className="flex items-center justify-between">
                        <Label htmlFor={`question-${index}`} className="text-base font-medium">
                          Question {index + 1}
                        </Label>
                        {newExam.questions.length > 1 && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => handleRemoveQuestion(index, false)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                      <Textarea
                        id={`question-${index}`}
                        value={question}
                        onChange={(e) => handleUpdateQuestion(index, e.target.value, false)}
                        placeholder={`Enter question ${index + 1}`}
                        className="min-h-[80px] w-full"
                      />
                    </div>
                  ))}
                </div>
              </CardContent>
              <CardFooter className="flex justify-between">
                <Button variant="outline" onClick={handleCancelCreate}>
                  Cancel
                </Button>
                <Button onClick={handleSaveNewExam}>
                  <Save className="w-4 h-4 mr-2" />
                  Create Exam
                </Button>
              </CardFooter>
            </Card>
          ) : isAssigning ? (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Assign Exam to Clients</CardTitle>
                    <CardDescription>
                      Select clients to assign "{assigningExam?.title}" to
                    </CardDescription>
                  </div>
                  <Button variant="outline" onClick={handleBackToExams}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Select Clients</Label>
                  <div className="grid gap-2 max-h-60 overflow-y-auto">
                    {clients.map((client) => (
                      <div key={client._id} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id={client._id}
                          checked={selectedClients.includes(client._id!)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedClients([...selectedClients, client._id!]);
                            } else {
                              setSelectedClients(selectedClients.filter(id => id !== client._id));
                            }
                          }}
                        />
                        <Label htmlFor={client._id} className="flex-1 cursor-pointer">
                          {client.name} ({client.email})
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="due-date">Due Date (Optional)</Label>
                  <Input
                    id="due-date"
                    type="datetime-local"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                  />
                </div>
              </CardContent>
              <CardFooter className="flex justify-between">
                <Button variant="outline" onClick={handleBackToExams}>
                  Cancel
                </Button>
                <Button onClick={handleSaveAssignment} disabled={selectedClients.length === 0}>
                  <Users className="w-4 h-4 mr-2" />
                  Assign to {selectedClients.length} Client{selectedClients.length !== 1 ? 's' : ''}
                </Button>
              </CardFooter>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <Button variant="outline" onClick={handleBackToExams} size="sm">
                      ← Back
                    </Button>
                    <div>
                      <CardTitle>
                        {isEditing ? (
                          <Input
                            value={editingExam?.title || ""}
                            onChange={(e) => setEditingExam(editingExam ? { ...editingExam, title: e.target.value } : null)}
                            className="text-xl font-bold"
                          />
                        ) : (
                          selectedExamData?.title
                        )}
                      </CardTitle>
                      <CardDescription>
                        {isEditing ? "Edit exam details" : "Answer all questions below"}
                      </CardDescription>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {!isEditing && (
                      <>
                        <Button variant="outline" onClick={handleEditExam}>
                          <Edit className="w-4 h-4 mr-2" />
                          Edit
                        </Button>
                        <Button variant="outline" onClick={handleAssignExam}>
                          <Users className="w-4 h-4 mr-2" />
                          Assign
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-medium">Questions</Label>
                  {isEditing && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleAddQuestion(true)}
                      className="flex items-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Add Question
                    </Button>
                  )}
                </div>
                {(isEditing ? editingExam?.questions : selectedExamData?.questions)?.map((question, index) => (
                  <div key={index} className="space-y-3">
                    <div className="space-y-3">
                      {isEditing ? (
                        <div className="space-y-2 p-4 border rounded-lg">
                          <div className="flex items-center justify-between">
                            <Label htmlFor={`edit-question-${index}`} className="text-base font-medium">
                              Question {index + 1}
                            </Label>
                            {(editingExam?.questions.length || 0) > 1 && (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => handleRemoveQuestion(index, true)}
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                          <Textarea
                            id={`edit-question-${index}`}
                            value={question.question}
                            onChange={(e) => handleUpdateQuestion(index, e.target.value, true)}
                            placeholder={`Enter question ${index + 1}`}
                            className="min-h-[80px] w-full"
                          />
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-sm bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0">
                              {index + 1}
                            </span>
                            <p className="font-medium text-base">{question.question}</p>
                          </div>
                          <div className="ml-8">
                            <Textarea
                              placeholder="Write your answer here..."
                              className="min-h-[120px] w-full"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </CardContent>
              <CardFooter className="flex justify-between">
                {isEditing ? (
                  <>
                    <Button variant="outline" onClick={handleCancelEdit}>
                      Cancel
                    </Button>
                    <Button onClick={handleSaveEdit}>
                      <Save className="w-4 h-4 mr-2" />
                      Save Changes
                    </Button>
                  </>
                ) : (
                  <>
                    <Button variant="outline" onClick={handleBackToExams}>
                      ← Back to Questions Center
                    </Button>
                    <div className="flex gap-2">
                      <Button variant="outline">
                        Save Draft
                      </Button>
                      <Button>Submit Exam</Button>
                    </div>
                  </>
                )}
              </CardFooter>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="completed" className="mt-6">
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold">Assigned Exams</h2>
              <Badge variant="secondary">{assignedExams.length} assigned</Badge>
            </div>

            {assignedExams.length === 0 ? (
              <Card className="card-wellness">
                <CardContent className="p-8 text-center">
                  <CheckCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-foreground mb-2">No Assigned Exams</h3>
                  <p className="text-muted-foreground">When you assign exams to clients, they will appear here.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {assignedExams.map((exam) => (
                  <Card key={exam._id} className="card-wellness hover:shadow-md transition-shadow">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="text-lg">{exam.title}</CardTitle>
                          <CardDescription>
                            Assigned to: {typeof exam.assignedTo === 'object' ? exam.assignedTo.name : 'Unknown Client'}
                            {exam.originalTemplate && typeof exam.originalTemplate === 'object' && (
                              <span className="ml-2 text-xs bg-muted px-2 py-1 rounded">
                                Template: {exam.originalTemplate.title}
                              </span>
                            )}
                          </CardDescription>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge
                            variant="outline"
                            className={
                              exam.status === 'completed'
                                ? "text-green-600 border-green-600"
                                : exam.status === 'in_progress'
                                  ? "text-yellow-600 border-yellow-600"
                                  : exam.status === 'overdue'
                                    ? "text-red-600 border-red-600"
                                    : "text-blue-600 border-blue-600"
                            }
                          >
                            {exam.status === 'completed' ? 'Completed' :
                              exam.status === 'in_progress' ? 'In Progress' :
                                exam.status === 'overdue' ? 'Overdue' : 'Assigned'}
                          </Badge>
                          {exam.submittedAt && (
                            <span className="text-sm text-muted-foreground">
                              Completed: {new Date(exam.submittedAt).toLocaleDateString()}
                            </span>
                          )}
                          {exam.assignedAt && !exam.submittedAt && (
                            <span className="text-sm text-muted-foreground">
                              Assigned: {new Date(exam.assignedAt).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {exam.status === 'completed' && exam.answers && exam.answers.length > 0 ? (
                        <div className="space-y-3">
                          <h4 className="font-medium text-sm text-muted-foreground mb-2">Answers:</h4>
                          {exam.answers.map((answer, index) => (
                            <div key={answer.questionId} className="border-l-2 border-green-500 pl-4">
                              <p className="font-medium text-sm text-muted-foreground mb-1">
                                Question {index + 1}
                              </p>
                              <p className="text-sm">{answer.answer}</p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-4">
                          <p className="text-sm text-muted-foreground">
                            {exam.status === 'completed' ? 'No answers submitted' : 'Waiting for client to complete'}
                          </p>
                        </div>
                      )}
                    </CardContent>
                    <CardFooter className="flex justify-between">
                      <div className="text-sm text-muted-foreground">
                        {exam.questions.length} questions
                      </div>
                      {exam.score && (
                        <Badge variant="secondary">
                          Score: {exam.score}%
                        </Badge>
                      )}
                    </CardFooter>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </TabsContent>


      </Tabs>
    </div>
  );
};

export default Settings;
